import React from "react";

import { Breadcrumb } from "../../../../atoms/breadcrumb";
import {
    PAGE_NAME,
    PERMISSIONS,
    TICKETS_STATUS_OPTIONS,
} from "../../../../../constants/constants";
import {
    useCancelRequestMutation,
    useChangeRequestStatusMutation,
    useSingleRequestQuery,
    useSyncWatchersMutation,
} from "../../../../../store/requestManagement/requestSlice";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "../../../../atoms/skeleton";
import {
    areArraysEqual,
    formattedDateTime,
    hasPermission,
} from "../../../../../utils";
import { Modal } from "../../../../molecules/modal";
import { Comments } from "../../../../organisms/requestManagement/comments";
import { Dropdown } from "../../../../atoms/dropdown";
import { Dialog } from "../../../../atoms/dialog";
import { toast } from "react-toastify";
import { useActiveUserListQuery } from "../../../../../store/user/userSlice";
import { payloadType, ResponseType, userStateType } from "../../../../../types";
import { Icon } from "../../../../atoms/icon";
import { Loader } from "../../../../atoms/loader";
import { useSelector } from "react-redux";

const RequestDetails = () => {
    const pageClassName = "p-requestDetails";
    const id = useParams<{ id: string }>().id;
    const navigate = useNavigate();
    const [isOpenComment, setIsOpenComment] = React.useState(false);
    const [comments, setComments] = React.useState<any[]>([]);
    const [requestId, setRequestId] = React.useState<string>("");

    const [cancelRequestApi, { isLoading: statusLoading }] =
        useCancelRequestMutation();
    const [changeStatusAPI] =
        useChangeRequestStatusMutation();
    const [syncWatchersAPI, { isLoading: watcherLoading }] =
        useSyncWatchersMutation();
    const { isLoading, isFetching, isSuccess, data, isError } = useSingleRequestQuery(
        id as string
    );
    const { data: userList, isLoading: usersLoading } = useActiveUserListQuery(
        {}
    );

    const [isCanceledDialogOpen, setIsCanceledDialogOpen] = React.useState(false);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [reviewData, setReviewData] = React.useState({
        status: "",
        id: undefined as string | undefined,
    });

    const [payload, setPayload] = React.useState<payloadType>({
        id: {
            value: id as string,
            error: "",
            isError: false,
        },
        watchers: {
            value: [] as { label: string; value: string }[],
            error: "",
            isError: false,
        },
    });

    React.useEffect(() => {
        setPayload({
            ...payload,
            watchers: {
                value: data?.result?.watchers?.map((watcher: any) => {
                    return {
                        label: watcher.firstName + " " + watcher.lastName,
                        value: watcher.id,
                    };
                }),
                error: "",
                isError: false,
            },
        });

        if (isError) {
            navigate("/404", { replace: true });
            toast.error("Request not found");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isError]);

    const closeDialogHandler = () => {
        setIsDialogOpen(!isDialogOpen);
        setReviewData({
            status: "",
            id: undefined,
        });
    };

    const closeCanceledDialogHandler = () => {
        setIsCanceledDialogOpen(!isCanceledDialogOpen);
        setReviewData({
            status: "",
            id: undefined,
        });
    };

    const canceledStatusHandler = async () => {
        setIsCanceledDialogOpen(!isCanceledDialogOpen);
        const fromData = new FormData();

        fromData.append("status", reviewData.status);
        fromData.append("id", reviewData.id?.toString() as string);

        const response = (await cancelRequestApi(fromData)) as any;

        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            toast.error(response?.error?.data?.message);
        }
    };

    const changeStatusHandler = async () => {
        setIsDialogOpen(!isDialogOpen);
        const fromData = new FormData();

        fromData.append("status", reviewData.status);
        fromData.append("id", reviewData.id?.toString() as string);
        fromData.append("type", "customerResponse");

        let response = (await changeStatusAPI(fromData)) as any;

        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            toast.error(response?.error?.data?.message);
        }
    };

    const watcherChangeHandler = (e: any) => {
        setPayload({
            ...payload,
            watchers: {
                value: e,
                error: "",
                isError: false,
            },
        });
    };

    const resetWatcher = () => {
        setPayload({
            ...payload,
            watchers: {
                value: data?.result?.watchers?.map((watcher: any) => {
                    return {
                        label: watcher.firstName + " " + watcher.lastName,
                        value: watcher.id,
                    };
                }),
                error: "",
                isError: false,
            },
        });
    };

    const watchersSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData();

        formData.append("id", id as string);
        (payload.watchers.value as { label: string; value: string }[]).forEach(
            (watcher) => {
                formData.append("watchers[]", watcher?.value);
            }
        );
        const response = (await syncWatchersAPI(formData)) as ResponseType;
        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.data?.message);
            }
        }
    };
    const user = useSelector((state: userStateType) => state.user.user);

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.REQUEST_DETAILS}</p>
            <Loader isLoading={statusLoading || watcherLoading}>
                <div className={`${pageClassName}__container`}>
                    {(hasPermission(
                        PERMISSIONS.APPROVAL_MANAGEMENT_APPROVED_VIEW.title
                    ) ||
                        data?.result?.requester?.id === user?.id) &&
                        isSuccess &&
                        ["In Review", "Approved"].includes(data?.result?.status) && (
                            <div className={`${pageClassName}__container__status`}>
                                <Dropdown
                                    options={[{ label: "Canceled", value: "Canceled" }]}
                                    selected={{ label: "Action", value: "" }}
                                    placeholder="Cancel Request"
                                    onClickChange={(e) => {
                                        setIsCanceledDialogOpen(!isCanceledDialogOpen);
                                        setReviewData({
                                            ...reviewData,
                                            status: e.value as string,
                                            id: data?.result?.id,
                                        });
                                    }}
                                    isBordered
                                    isIcon
                                />
                            </div>
                        )}

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Request ID
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? <Skeleton height="small" /> : data?.result?.id}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Type</p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.type ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Sub Type
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.subType ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Status</p>
                        <p className={`${pageClassName}__container__item__status`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.status ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Requester
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.requester ? (
                                data?.result?.requester?.middleName ? (
                                    `${data?.result?.requester?.firstName} ${data?.result?.requester?.middleName} ${data?.result?.requester?.lastName}`
                                ) : (
                                    `${data?.result?.requester?.firstName} ${data?.result?.requester?.lastName}`
                                )
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>

                    {data?.result?.notes && (
                        <div className={`${pageClassName}__container__item`}>
                            <p className={`${pageClassName}__container__item__title`}>
                                Notes
                            </p>
                            <p
                                style={{
                                    whiteSpace: 'pre-line',
                                    overflowWrap: 'break-word',
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-all'
                                }}
                                className={`${pageClassName}__container__item__value`}
                                dangerouslySetInnerHTML={{ __html: data?.result?.notes }}
                            />
                        </div>
                    )}

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Created At
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.createdAt ? (
                                formattedDateTime(data?.result?.createdAt)
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Watchers
                        </p>
                        {usersLoading ? (
                            <Skeleton height="small" />
                        ) : (
                            <form
                                className={`${pageClassName}__watchers`}
                                onSubmit={watchersSubmitHandler}
                            >
                                <Dropdown
                                    className={`${pageClassName}__watchers__dropdown`}
                                    options={userList?.result?.data?.map((user: any) => {
                                        return {
                                            label: user.firstName + " " + user.lastName,
                                            value: user.id,
                                        };
                                    })}
                                    isMulti
                                    selected={
                                        payload.watchers.value as { label: string; value: string }[]
                                    }
                                    placeholder="Please select watchers"
                                    onClickChange={watcherChangeHandler}
                                />

                                <div className={`${pageClassName}__watchers__action`}>
                                    <button
                                        className={`${pageClassName}__watchers__action__btn`}
                                        type="submit"
                                        disabled={
                                            areArraysEqual(
                                                payload.watchers.value as [],
                                                data?.result?.watchers?.map((watcher: any) => {
                                                    return {
                                                        label: watcher.firstName + " " + watcher.lastName,
                                                        value: watcher.id,
                                                    };
                                                })
                                            ) || isFetching
                                        }
                                    >
                                        <Icon icon="check" size="secondary" />
                                    </button>
                                    <button
                                        className={`${pageClassName}__watchers__action__btn ${pageClassName}__watchers__action__btn--cancel`}
                                        type="button"
                                        onClick={() => resetWatcher()}
                                        disabled={
                                            areArraysEqual(
                                                payload.watchers.value as [],
                                                data?.result?.watchers?.map((watcher: any) => {
                                                    return {
                                                        label: watcher.firstName + " " + watcher.lastName,
                                                        value: watcher.id,
                                                    };
                                                })
                                            ) || isFetching
                                        }
                                    >
                                        <Icon icon="cancel" size="secondary" />
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    <p className={`${pageClassName}__container__userRequest__header`}>
                        Requests
                    </p>
                    <div
                        className={`${pageClassName}__container__userRequest ${data?.result?.subType === "Configuration"
                            ? `${pageClassName}__container__userRequest--configuration`
                            : ""
                            }`}
                    >
                        {data?.result?.requests?.map((request: any, index: number) => (
                            <div
                                className={`${pageClassName}__container__userRequest__content`}
                                key={index}
                            >
                                <div
                                    className={`${pageClassName}__container__userRequest__content__container`}
                                >
                                    {request?.status === "Waiting for Customer Response" && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__content__action`}
                                        >
                                            <Dropdown
                                                options={TICKETS_STATUS_OPTIONS(request?.status)}
                                                selected={{ label: "Action", value: "" }}
                                                placeholder="Review"
                                                onClickChange={(e) => {
                                                    setIsDialogOpen(!isDialogOpen);
                                                    setReviewData({
                                                        ...reviewData,
                                                        status: e.value as string,
                                                        id: request?.id,
                                                    });
                                                }}
                                                isBordered
                                                isIcon
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`${pageClassName}__container__userRequest__item`}
                                    >
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__title`}
                                        >
                                            Sub Request ID
                                        </p>
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__value`}
                                        >
                                            {request?.id}
                                        </p>
                                    </div>
                                    {request?.firstName && request?.lastName && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                User Name
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.firstName} {request?.lastName}
                                            </p>
                                        </div>
                                    )}

                                    {request?.firstName && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                First Name
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.firstName}
                                            </p>
                                        </div>
                                    )}

                                    {request?.lastName && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Last Name
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.lastName}
                                            </p>
                                        </div>
                                    )}

                                    {request?.designation && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Designation
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.designation}
                                            </p>
                                        </div>
                                    )}

                                    {request?.address && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Address
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.address}
                                            </p>
                                        </div>
                                    )}

                                    {request?.organization && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Organization
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.organization?.name}
                                            </p>
                                        </div>
                                    )}

                                    {request?.department && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Department
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.department}
                                            </p>
                                        </div>
                                    )}

                                    {request?.section && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Section
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.section}
                                            </p>
                                        </div>
                                    )}

                                    {request?.projects?.length > 0 && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Projects
                                            </p>
                                            <div
                                                className={`${pageClassName}__container__userRequest__item__list`}
                                            >
                                                {request?.projects.map(
                                                    (project: any, index: number) => (
                                                        <li key={index}>{project}</li>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {request?.manager && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                PIC/Manager
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.manager?.middleName
                                                    ? `${request?.manager?.firstName} ${request?.manager?.middleName} ${request?.manager?.lastName} (${request?.manager?.email})`
                                                    : `${request?.manager?.firstName} ${request?.manager?.lastName} (${request?.manager?.email})`}
                                            </p>
                                        </div>
                                    )}

                                    {request?.email && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Email
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.email}
                                            </p>
                                        </div>
                                    )}

                                    <div
                                        className={`${pageClassName}__container__userRequest__item`}
                                    >
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__title`}
                                        >
                                            SaaS Tool
                                        </p>
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__value`}
                                        >
                                            {request?.saasTool}
                                        </p>
                                    </div>

                                    {request?.type && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Type
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.type}
                                            </p>
                                        </div>
                                    )}

                                    {request?.type && request?.name && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                {request?.type} Name
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.name}
                                            </p>
                                        </div>
                                    )}

                                    {request?.type && request?.newName && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                {request?.type} New Name
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.newName}
                                            </p>
                                        </div>
                                    )}

                                    {request?.role && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Role
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.role}
                                            </p>
                                        </div>
                                    )}
                                    {request?.templateType && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Template Type
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.templateType}
                                            </p>
                                        </div>
                                    )}
                                    {request?.approvers?.length > 0 && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Approvers
                                            </p>
                                            <div
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.approvers.map(
                                                    (approver: any, index: number) => (
                                                        <p key={index}>
                                                            {approver?.middleName
                                                                ? `${approver?.firstName} ${approver?.middleName} ${approver?.lastName}`
                                                                : `${approver?.firstName} ${approver?.lastName}`}
                                                        </p>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        className={`${pageClassName}__container__userRequest__item`}
                                    >
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__title`}
                                        >
                                            Status
                                        </p>
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__status`}
                                        >
                                            {request?.status}
                                        </p>
                                    </div>

                                    {request?.reviewedBy && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Reviewed By
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.reviewedBy?.middleName
                                                    ? `${request?.reviewedBy?.firstName} ${request?.reviewedBy?.middleName} ${request?.reviewedBy?.lastName}`
                                                    : `${request?.reviewedBy?.firstName} ${request?.reviewedBy?.lastName}`}
                                            </p>
                                        </div>
                                    )}

                                    {request?.issueSummary && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Issue Summary
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.issueSummary}
                                            </p>
                                        </div>
                                    )}

                                    {request?.instruction && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Issue Description
                                            </p>
                                            <p
                                                style={{
                                                    whiteSpace: 'pre-line',
                                                    overflowWrap: 'break-word',
                                                    wordWrap: 'break-word',
                                                    wordBreak: 'break-all'
                                                }}
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                                dangerouslySetInnerHTML={{
                                                    __html: request?.instruction,
                                                }}
                                            />
                                        </div>
                                    )}

                                    {request?.files && (
                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Attachments
                                            </p>
                                            <div
                                                className={`${pageClassName}__container__userRequest__item__files`}
                                            >
                                                {request?.files.map((file: any, index: number) => (
                                                    <a
                                                        href={file.basePath + "/" + file?.filePath}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        key={index}
                                                        className={`${pageClassName}__container__userRequest__item__files__file`}
                                                    >
                                                        {file?.fileName}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        className={`${pageClassName}__container__userRequest__item`}
                                    >
                                        <p
                                            className={`${pageClassName}__container__userRequest__item__title`}
                                        >
                                            Comments
                                        </p>
                                        <div
                                            className={`${pageClassName}__container__userRequest__item__commentsContainer`}
                                        >
                                            <button
                                                className={`${pageClassName}__container__userRequest__item__comments`}
                                                onClick={() => {
                                                    setIsOpenComment(true);
                                                    setComments(request?.comments);
                                                    setRequestId(request?.id);
                                                }}
                                            >
                                                {request?.comments?.length}
                                            </button>
                                            <button
                                                className={`${pageClassName}__container__userRequest__item__details`}
                                                onClick={() => navigate(`/comments/${request?.id}`)}
                                            >
                                                Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Loader>
            <Dialog
                header={`${reviewData.status} Request`}
                body={
                    <div className={`${pageClassName}__dialog`}>
                        <p className={`${pageClassName}__dialog__text`}>
                            Are you sure you want to {reviewData.status.toLowerCase()} this
                            request?
                        </p>
                        <p className={`${pageClassName}__dialog__text`}>
                            This action cannot be undone.
                        </p>
                    </div>
                }
                isOpen={isCanceledDialogOpen}
                onClose={closeCanceledDialogHandler}
                successFn={canceledStatusHandler}
                btn={reviewData.status}
                icon={reviewData.status === "Closed" ? "approved" : "rejected"}
                color={reviewData.status === "Closed" ? "green" : undefined}
            />

            <Dialog
                header={`${reviewData.status} Request`}
                body={
                    <div className={`${pageClassName}__dialog`}>
                        <p className={`${pageClassName}__dialog__text`}>
                            Are you sure you want to {reviewData.status.toLowerCase()} this
                            request?
                        </p>
                        <p className={`${pageClassName}__dialog__text`}>
                            This action cannot be undone.
                        </p>
                    </div>
                }
                isOpen={isDialogOpen}
                onClose={closeDialogHandler}
                successFn={changeStatusHandler}
                btn={reviewData.status}
                icon={
                    [
                        "Closed",
                        "In Progress",
                        "Waiting for Customer Response",
                        "Fixed",
                    ].includes(reviewData.status)
                        ? "approved"
                        : "rejected"
                }
                color={
                    [
                        "Closed",
                        "In Progress",
                        "Waiting for Customer Response",
                        "Fixed",
                    ].includes(reviewData.status)
                        ? "green"
                        : undefined
                }
            />

            <Modal
                isOpen={isOpenComment}
                onClose={() => setIsOpenComment(false)}
                header="Comments"
            >
                <Comments
                    comments={comments}
                    setComments={setComments}
                    requestId={requestId}
                />
            </Modal>
        </div>
    );
};

export default RequestDetails;
